import { useCallback, useEffect, useState } from 'react';
import type { SignCategory, SignCategoryLogEntry, SignTag } from '@todo/shared';
import { ApiError } from '../api/client';
import { signApi } from '../api/sign.api';
import { SidePanel } from './SidePanel';

/**
 * Manage CG Sign's categories and tags: rename, delete (categories can be
 * merged into another instead), and read the change history.
 *
 * A rename needs no follow-up anywhere else: documents point at the
 * category / tag by id, so every document - old or new - shows the new name
 * the next time it is loaded. `onChanged` just asks the page to reload.
 */

type Tab = 'categories' | 'tags' | 'history';

const ACTION_LABEL: Record<SignCategoryLogEntry['action'], string> = {
  CREATED: 'Category created',
  RENAMED: 'Category renamed',
  DELETED: 'Category deleted',
  MERGED: 'Category merged',
  TAG_CREATED: 'Tag created',
  TAG_RENAMED: 'Tag renamed',
  TAG_DELETED: 'Tag deleted',
};

function when(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    .format(new Date(iso));
}

interface Item { id: string; name: string; documentCount?: number; elsewhereCount?: number }

function Row({
  item,
  kind,
  mergeTargets,
  onRename,
  onRemove,
}: {
  item: Item;
  kind: 'category' | 'tag';
  mergeTargets: Item[];
  onRename: (id: string, name: string) => Promise<void>;
  onRemove: (id: string, mergeInto?: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [confirming, setConfirming] = useState(false);
  const [mergeInto, setMergeInto] = useState('');
  const [confirmRename, setConfirmRename] = useState(false);
  const [busy, setBusy] = useState(false);
  const count = item.documentCount ?? 0;
  const elsewhereN = item.elsewhereCount ?? 0;
  const tagInUse = kind === 'tag' && count + elsewhereN > 0;
  const usedOn = [
    count > 0 ? `${count} ${count === 1 ? 'CG Sign document' : 'CG Sign documents'}` : '',
    elsewhereN > 0 ? `${elsewhereN} ${elsewhereN === 1 ? 'Folder file' : 'Folder files'}` : '',
  ].filter(Boolean).join(' and ');

  async function save(confirmed = false) {
    const next = name.trim();
    if (!next || next === item.name) { setEditing(false); setConfirmRename(false); setName(item.name); return; }
    // A rename of a tag that is on anything shows where it applies first.
    if (tagInUse && !confirmed) { setConfirmRename(true); return; }
    setBusy(true);
    try { await onRename(item.id, next); setEditing(false); setConfirmRename(false); }
    finally { setBusy(false); }
  }

  async function remove() {
    setBusy(true);
    try { await onRemove(item.id, mergeInto || undefined); }
    finally { setBusy(false); setConfirming(false); }
  }

  return (
    <li className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex items-center gap-2">
        {editing ? (
          <input
            autoFocus
            value={name}
            maxLength={kind === 'tag' ? 40 : 80}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save();
              if (e.key === 'Escape') { setEditing(false); setName(item.name); }
            }}
            className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm outline-none focus:border-brand-500"
          />
        ) : (
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
            {kind === 'tag' ? '#' : ''}{item.name}
          </span>
        )}
        <span className="shrink-0 text-xs text-slate-400">
          {count} {count === 1 ? 'document' : 'documents'}
        </span>
        {editing ? (
          <>
            <button type="button" disabled={busy} onClick={() => void save()}
              className="rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">Save</button>
            <button type="button" onClick={() => { setEditing(false); setConfirmRename(false); setName(item.name); }}
              className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">Cancel</button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setEditing(true)} aria-label={`Rename ${item.name}`}
              className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-slate-100">Rename</button>
            <button type="button" onClick={() => setConfirming(true)} aria-label={`Delete ${item.name}`}
              className="rounded-md px-2 py-1 text-xs text-red-600 hover:bg-red-50">Delete</button>
          </>
        )}
      </div>

      {confirmRename && (
        <div className="mt-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-900">
          <p>
            Renaming applies everywhere this tag is used: {usedOn}. The tag list is shared with Folder,
            so they all show the new name.
          </p>
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={busy} onClick={() => void save(true)}
              className="rounded-md bg-brand-600 px-2.5 py-1 font-semibold text-white disabled:opacity-50">Rename everywhere</button>
            <button type="button" onClick={() => setConfirmRename(false)}
              className="rounded-md px-2.5 py-1 text-slate-600 hover:bg-white">Cancel</button>
          </div>
        </div>
      )}

      {confirming && kind === 'tag' && tagInUse && (
        <div className="mt-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-900">
          <p>This tag can't be deleted while it is in use: {usedOn}. Remove it from them first.</p>
          <div className="mt-2">
            <button type="button" onClick={() => setConfirming(false)}
              className="rounded-md px-2.5 py-1 text-slate-600 hover:bg-white">Close</button>
          </div>
        </div>
      )}

      {confirming && !(kind === 'tag' && tagInUse) && (
        <div className="mt-2 rounded-md bg-red-50 p-2.5 text-xs text-red-800">
          {kind === 'category' ? (
            <>
              <p>
                {count > 0
                  ? `${count} ${count === 1 ? 'document is' : 'documents are'} filed under this category. Move them to:`
                  : 'No documents use this category.'}
              </p>
              {count > 0 && (
                <select
                  value={mergeInto}
                  onChange={(e) => setMergeInto(e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-red-200 bg-white px-2 py-1 text-xs text-slate-800"
                >
                  <option value="">Leave them uncategorised</option>
                  {mergeTargets.filter((t) => t.id !== item.id).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              )}
            </>
          ) : (
            <p>No document or file uses this tag. Delete it?</p>
          )}
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={busy} onClick={() => void remove()}
              className="rounded-md bg-red-600 px-2.5 py-1 font-semibold text-white disabled:opacity-50">
              {mergeInto ? 'Merge and delete' : 'Delete'}
            </button>
            <button type="button" onClick={() => setConfirming(false)}
              className="rounded-md px-2.5 py-1 text-slate-600 hover:bg-white">Keep it</button>
          </div>
        </div>
      )}
    </li>
  );
}

export function SignLabelsManager({
  open,
  onClose,
  categories,
  tags,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  categories: SignCategory[];
  tags: SignTag[];
  /** Reload whatever shows names: the picker options and the list. */
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<Tab>('categories');
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<SignCategoryLogEntry[] | null>(null);

  const loadLog = useCallback(() => {
    signApi.categoryLog().then(setLog).catch(() => setLog([]));
  }, []);

  useEffect(() => {
    if (open && tab === 'history') loadLog();
  }, [open, tab, loadLog]);

  useEffect(() => { if (open) { setError(null); setLog(null); } }, [open]);

  async function guard(work: () => Promise<void>) {
    setError(null);
    try {
      await work();
      onChanged();
      setLog(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work - try again.');
    }
  }

  const list: Item[] = tab === 'tags' ? tags : categories;

  return (
    <SidePanel
      open={open}
      title="Categories and tags"
      description="Rename or delete them, and see who changed what. Renames show on every document."
      onClose={onClose}
    >
      <div className="flex h-full flex-col px-6 py-5">
        <div className="mb-3 flex gap-1 rounded-lg bg-slate-100 p-1 text-sm">
          {(['categories', 'tags', 'history'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { setTab(t); setError(null); }}
              className={`flex-1 rounded-md px-3 py-1.5 font-medium capitalize ${tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              {t}
            </button>
          ))}
        </div>

        {error && (
          <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">{error}</p>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {tab !== 'history' && (
            list.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">
                {tab === 'tags' ? 'No tags yet - add them on a document.' : 'No categories yet.'}
              </p>
            ) : (
              <ul className="space-y-2">
                {list.map((item) => (
                  <Row
                    key={`${tab}-${item.id}`}
                    item={item}
                    kind={tab === 'tags' ? 'tag' : 'category'}
                    mergeTargets={categories}
                    onRename={(id, name) => guard(async () => {
                      if (tab === 'tags') await signApi.renameTag(id, name);
                      else await signApi.renameCategory(id, name);
                    })}
                    onRemove={(id, mergeInto) => guard(async () => {
                      if (tab === 'tags') await signApi.removeTag(id);
                      else await signApi.removeCategory(id, mergeInto);
                    })}
                  />
                ))}
              </ul>
            )
          )}

          {tab === 'history' && (
            log === null ? (
              <p className="py-6 text-center text-sm text-slate-400">Loading…</p>
            ) : log.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">Nothing has been changed yet.</p>
            ) : (
              <ul className="space-y-2">
                {log.map((e) => (
                  <li key={e.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                    <p className="font-medium text-slate-800">
                      {ACTION_LABEL[e.action] ?? e.action}
                      <span className="font-normal text-slate-600">
                        {e.oldName && e.newName ? ` - ${e.oldName} → ${e.newName}` : ''}
                        {e.oldName && !e.newName ? ` - ${e.oldName}` : ''}
                        {!e.oldName && e.newName ? ` - ${e.newName}` : ''}
                      </span>
                    </p>
                    {e.detail && <p className="text-xs text-slate-500">{e.detail}</p>}
                    <p className="text-xs text-slate-400">{e.actor} · {when(e.createdAt)}</p>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>
      </div>
    </SidePanel>
  );
}
